import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import styles from "../css/GadgetCard.module.css";
import {
  FlattenedGadget,
  Gadget,
  getFlattenedGadget,
} from "../../../../models/GadgetModel";
import { useEffect, useState } from "react";
import { FileType, createResourceEndpointUrl } from "../../../../helper/utils";
import DefaultGadgetIcon from "../../../../images/DefaultGadgetIcon.svg";
import { GadgetPlaform, PlatformIcon } from "./GadgetPlaform";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { useLoginValidator } from "../../../GadgetDetails/hooks/useLoginValidator";
import { Tooltip, Typography } from "@mui/material";
import { CustomModal } from "../../../../components/Modal/CustomModal";
import { ConnectedDeviceList } from "../../../GadgetDetails/components/ConnectedDeviceList";

interface GadgetActionIconProps {
  children: React.ReactNode;
}

const GadgetActionIcon = ({ children }: GadgetActionIconProps) => {
  const theme = useTheme();

  return (
    <IconButton
      aria-label="download"
      sx={{
        paddingLeft: "0px",
        color: "#333333",
        ":hover": { color: theme.palette.primary.main },
      }}
      disableRipple
    >
      {children}
    </IconButton>
  );
};

interface GadgetCardProps {
  gadget: Gadget;
}

export default function GadgetCard({ gadget }: GadgetCardProps) {
  const navigate = useNavigate();
  const [iconFile, setIconFile] = useState<string>("");

  const [displayGadget, setDisplayGadget] = useState<FlattenedGadget>();
  const { validateLogin } = useLoginValidator();
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleClick = () => navigate(`/gadgets/${gadget.id}`);
  const handleDownload = (event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    validateLogin(handleOpenModal, []);
  };

  useEffect(() => {
    const flattenedGadget = getFlattenedGadget(gadget);
    const iconUrl = flattenedGadget.icon
      ? createResourceEndpointUrl(
          flattenedGadget.id,
          flattenedGadget.version,
          flattenedGadget.platform,
          FileType.ICON,
          flattenedGadget.icon
        )
      : DefaultGadgetIcon;
    setIconFile(iconUrl);
    flattenedGadget.icon = iconUrl;
    setDisplayGadget(flattenedGadget);
  }, [gadget]);

  return (
    <>
      <Card className={styles.card} onClick={handleClick}>
        <img
          className={styles.gadgetIcon}
          data-testid="gadget-icon"
          alt={gadget.name + " icon"}
          src={iconFile}
        />
        <Box className={styles.cardContentContainer}>
          <CardContent className={styles.cardContent}>
            <div className={styles.centerText}>
              <Tooltip title={gadget.name}>
                <Typography variant="body2" className={styles.gadgetName}>{gadget.name}</Typography>
              </Tooltip>
            </div>
            <div className={styles.centerText}>
              <Tooltip title={gadget.developer_name}>
                <Typography variant="body2" className={styles.provider}>By {gadget.developer_name}</Typography>
              </Tooltip>
            </div>
            {/* <SupportedOS name={gadget.supportedOs} /> */}
            <Box className={styles.cardActions}>
              <GadgetActionIcon>
                <Tooltip title="Install">
                  <FontAwesomeIcon icon={faDownload} onClick={handleDownload} />
                </Tooltip>
              </GadgetActionIcon>
              {/* <GadgetActionIcon>
                <AddShoppingCartIcon fontSize="large" />
              </GadgetActionIcon> */}
              <div
                className={styles.platformIconContainer}
                title={gadget.supportedOs}
              >
                <PlatformIcon platform={GadgetPlaform[gadget.supportedOs as keyof typeof GadgetPlaform]} size="2xl" />
              </div>
            </Box>
          </CardContent>
        </Box>
      </Card>
      {displayGadget && (
        <CustomModal
          open={openModal}
          handleClose={handleCloseModal}
          children={<ConnectedDeviceList displayGadget={displayGadget} />}
        />
      )}
    </>
  );
}
